import { useState } from 'react';
import { SPORTS } from '../data/constants';

function downloadCSV(content, filename) {
    const bom = '﻿';
    const blob = new Blob([bom + content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
}

function csvCell(value) {
    const text = value === undefined || value === null ? '' : String(value);
    return `"${text.replace(/"/g, '""')}"`;
}

function techniqueText(r) {
    if (!r.techniques) return '';
    if (!Array.isArray(r.techniques)) return r.techniques;
    return r.techniques
        .filter(t => t.name || t.point || t.position)
        .map(t => [t.name, t.position && `(${t.position})`, t.point].filter(Boolean).join(' '))
        .join(' / ');
}

function exerciseText(r) {
    if (r.exercises?.length) {
        return r.exercises.map(ex => {
            const sets = (ex.sets || []).map(s => `${s.reps || 0}x${s.weight || 0}kg`).join(' ');
            return [ex.name, ex.muscleGroup && `(${ex.muscleGroup})`, sets].filter(Boolean).join(' ');
        }).join(' / ');
    }
    return r.exerciseName || '';
}

function buddyText(r) {
    return (r.buddies || []).map(b => b.nickname || b.userId).join(' / ');
}

const USERID_REGEX = /^[a-z][a-z0-9_]{3,19}$/;

const checkStatusStyle = {
    available: { color: 'var(--accent)' },
    taken: { color: 'var(--coral)' },
    same: { color: 'var(--text3)' },
    invalid: { color: 'var(--coral)' },
};

const checkStatusMsg = {
    checking: '확인 중...',
    available: '사용 가능한 ID예요',
    taken: '이미 사용 중인 ID예요',
    same: '현재 사용 중인 ID예요',
    invalid: '영소문자로 시작, 영소문자·숫자·_ 조합, 4~20자',
};

function AccountPage({ nickname, setNickname, userId, checkUserIdAvailable, updateUserId, user, onLogout, showToast, onBack }) {
    const [nicknameInput, setNicknameInput] = useState(nickname || '');
    const [userIdInput, setUserIdInput] = useState(userId || '');
    const [checkStatus, setCheckStatus] = useState(null);

    const saveNickname = () => {
        const trimmed = nicknameInput.trim();
        if (!trimmed) { showToast('닉네임을 입력해주세요'); return; }
        setNickname(trimmed);
        showToast('닉네임 변경 완료!');
    };

    const handleUserIdInput = (val) => {
        setUserIdInput(val);
        setCheckStatus(null);
    };

    const handleCheck = async () => {
        const trimmed = userIdInput.trim();
        if (!USERID_REGEX.test(trimmed)) { setCheckStatus('invalid'); return; }
        setCheckStatus('checking');
        const result = await checkUserIdAvailable(trimmed);
        setCheckStatus(result);
    };

    const handleUpdateUserId = async () => {
        try {
            await updateUserId(userIdInput.trim());
            showToast('ID 변경 완료!');
            setCheckStatus(null);
        } catch {
            showToast('변경에 실패했어요. 다시 시도해 주세요.');
        }
    };

    const canSaveId = checkStatus === 'available';

    return (
        <>
            <button className="back-btn" onClick={onBack}>← 더보기</button>

            {/* 닉네임 */}
            <p className="section-label">닉네임</p>
            <div className="export-card">
                <div className="weight-row">
                    <div className="weight-input-wrap">
                        <input
                            className="form-input"
                            value={nicknameInput}
                            onChange={e => setNicknameInput(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && saveNickname()}
                            placeholder="닉네임 입력"
                            maxLength={20}
                        />
                    </div>
                    <button
                        className="tpl-btn primary"
                        style={{ flex: 0, padding: '10px 16px', whiteSpace: 'nowrap' }}
                        onClick={saveNickname}
                    >변경</button>
                </div>
            </div>

            {/* 나만의 ID */}
            <p className="section-label">나만의 ID</p>
            <div className="export-card">
                <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10, lineHeight: 1.6 }}>
                    친구 추가 시 사용할 고유 ID입니다.<br />
                    영소문자로 시작, 영소문자·숫자·_만 사용, 4~20자
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <div className="weight-input-wrap">
                        <input
                            className="form-input"
                            value={userIdInput}
                            onChange={e => handleUserIdInput(e.target.value)}
                            placeholder="my_id_123"
                            maxLength={20}
                            autoCapitalize="none"
                        />
                    </div>
                    <button
                        className="tpl-btn"
                        style={{ flex: 0, padding: '10px 14px', whiteSpace: 'nowrap' }}
                        onClick={handleCheck}
                    >중복 확인</button>
                </div>
                {checkStatus && (
                    <div style={{ fontSize: 12, ...checkStatusStyle[checkStatus] }}>
                        {checkStatusMsg[checkStatus]}
                    </div>
                )}
                {canSaveId && (
                    <button
                        className="tpl-btn primary"
                        style={{ width: '100%', padding: 12, marginTop: 10 }}
                        onClick={handleUpdateUserId}
                    >ID 변경하기</button>
                )}
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 10 }}>
                    현재 ID: <span style={{ fontFamily: 'DM Mono, monospace', color: 'var(--text2)' }}>{userId}</span>
                </div>
            </div>

            {/* 이메일 */}
            <p className="section-label">이메일</p>
            <div className="export-card">
                <div style={{ fontSize: 14, color: 'var(--text2)', fontFamily: 'DM Mono, monospace', wordBreak: 'break-all' }}>
                    {user?.email || '—'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>
                    Google 계정으로 로그인된 이메일입니다.
                </div>
            </div>

            {/* 로그아웃 */}
            <p className="section-label">계정</p>
            <div className="export-card">
                <button
                    className="tpl-btn del"
                    style={{ width: '100%', padding: 14, fontSize: 14 }}
                    onClick={onLogout}
                >
                    로그아웃
                </button>
            </div>

            <div style={{ height: 8 }} />
        </>
    );
}

function ExportPage({ records, weights, showToast, onBack }) {
    const exportCSV = () => {
        if (!records.length) { showToast('기록이 없어요'); return; }
        const header = '날짜,종목,세부종목,운동목표,함께운동,시간(분),그래플링종목,스파링,스파링라운드,스파링분,스파링상대,기술,부위,운동,유산소종류,거리,칼로리,컨디션,운동강도,일기';
        const rows = records.map(r => [
            r.date, SPORTS[r.sport]?.name || r.sport, r.sport === 'striking' ? (r.strType || '') : '', r.focus || '', buddyText(r), r.duration || '', r.sport === 'bjj' ? (r.gi || '') : '',
            r.sparring ? 'O' : '', r.sparringRounds || '', r.sparringMin || '',
            r.sparringPartner || '', techniqueText(r),
            r.muscleGroups?.join('/') || r.muscleGroup || '',
            exerciseText(r), r.cardioType || '', r.distance || '', r.calories || '',
            r.condScore || '', r.fatScore || '', r.diary || '',
        ].map(csvCell).join(','));
        downloadCSV([header, ...rows].join('\n'), 'workout_records.csv');
        showToast('다운로드 완료!');
    };

    const exportWeightCSV = () => {
        if (!weights.length) { showToast('체중 기록이 없어요'); return; }
        const sorted = [...weights].sort((a, b) => new Date(a.date) - new Date(b.date));
        const header = '날짜,체중(kg)';
        const rows = sorted.map(w => `${w.date},${w.val}`);
        downloadCSV([header, ...rows].join('\n'), 'weight_records.csv');
        showToast('다운로드 완료!');
    };

    return (
        <>
            <button className="back-btn" onClick={onBack}>← 더보기</button>

            <p className="section-label">운동 기록</p>
            <div className="export-card">
                <div className="export-title">운동 기록 CSV</div>
                <div className="export-desc">날짜, 종목, 운동 시간, 스파링, 컨디션, 피로도, 일기 등 모든 기록을 CSV 파일로 내보냅니다.</div>
                <button className="tpl-btn primary" style={{ width: '100%', padding: 12 }} onClick={exportCSV}>CSV 다운로드</button>
            </div>

            <p className="section-label">체중 기록</p>
            <div className="export-card">
                <div className="export-title">체중 기록 CSV</div>
                <div className="export-desc">날짜별 체중 기록을 CSV로 내보냅니다.</div>
                <button className="tpl-btn primary" style={{ width: '100%', padding: 12 }} onClick={exportWeightCSV}>체중 CSV 다운로드</button>
            </div>

            <div style={{ height: 8 }} />
        </>
    );
}

export default function More({ records, weights, nickname, setNickname, userId, checkUserIdAvailable, updateUserId, showToast, user, onLogout, initialView }) {
    const [view, setView] = useState(initialView || 'main');

    if (view === 'account') {
        return (
            <AccountPage
                nickname={nickname}
                setNickname={setNickname}
                userId={userId}
                checkUserIdAvailable={checkUserIdAvailable}
                updateUserId={updateUserId}
                user={user}
                onLogout={onLogout}
                showToast={showToast}
                onBack={() => setView('main')}
            />
        );
    }

    if (view === 'export') {
        return (
            <ExportPage
                records={records}
                weights={weights}
                showToast={showToast}
                onBack={() => setView('main')}
            />
        );
    }

    return (
        <>
            {/* 계정 관리 */}
            <p className="section-label">계정</p>
            <div
                className="export-card"
                style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                onClick={() => setView('account')}
            >
                <div>
                    <div className="export-title" style={{ marginBottom: 2 }}>계정 관리</div>
                    <div style={{ fontSize: 12, color: 'var(--text3)' }}>닉네임 · ID · 이메일 · 로그아웃</div>
                </div>
                <span style={{ color: 'var(--text3)', fontSize: 18 }}>›</span>
            </div>

            {/* 데이터 내보내기 */}
            <p className="section-label">데이터</p>
            <div
                className="export-card"
                style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                onClick={() => setView('export')}
            >
                <div>
                    <div className="export-title" style={{ marginBottom: 2 }}>데이터 내보내기</div>
                    <div style={{ fontSize: 12, color: 'var(--text3)' }}>운동 기록 · 체중 기록 CSV</div>
                </div>
                <span style={{ color: 'var(--text3)', fontSize: 18 }}>›</span>
            </div>

            <div style={{ height: 8 }} />
        </>
    );
}
